'use client';

import { usePathname, useRouter } from 'next/navigation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Globe } from 'lucide-react';
import { locales } from '@/i18n/config';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';

export default function LocaleSwitcher() {
  const pathname = usePathname();
  const router = useRouter();
  const [availableLocales, setAvailableLocales] = useState(locales);
  
  // Ensure locales are loaded properly
  useEffect(() => {
    console.log('Available locales:', locales);
    setAvailableLocales(locales);
  }, []);

  // Extract the current locale from the pathname
  const segments = pathname.split('/').filter(Boolean);
  const currentLocale = locales.includes(segments[0] as any) ? segments[0] : 'en';

  const switchLocale = (locale: string) => {
    if (locale === currentLocale) return; // Don't navigate if locale hasn't changed
    
    // Preserve the current path but change the locale
    let newPath: string;
    
    if (locales.includes(segments[0] as any)) {
      // If current path has a locale, replace it
      newPath = '/' + [locale, ...segments.slice(1)].join('/');
    } else {
      // If current path doesn't have a locale, add it
      newPath = '/' + [locale, ...segments].join('/');
    }
    
    // Ensure we don't end up with double slashes
    newPath = newPath.replace(/\/+/g, '/');
    
    // If we end up with just the locale, ensure there's a trailing slash
    if (newPath === `/${locale}`) {
      newPath = `/${locale}`;
    }
    
    router.push(newPath);
  };

  const getDisplayName = (locale: string) => {
    switch (locale) {
      case 'en':
        return 'English';
      case 'en-AU':
        return 'English (AU)';
      case 'en-NZ':
        return 'English (NZ)';
      default:
        return locale;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Globe className="h-4 w-4 text-gray-600" />
      <Select
        value={currentLocale}
        onValueChange={switchLocale}
      >
        <SelectTrigger className={cn(
          'w-[160px] h-8 text-sm font-bold',
          'border-0 shadow-none',
          'focus:ring-0 focus:ring-offset-0',
          'hover:bg-white/30 hover:text-gray-700',
          'focus:bg-white/30 focus:text-gray-700',
          'data-[state=open]:bg-white/30 data-[state=open]:text-gray-700',
          'transition-colors duration-200',
          'px-3 grid grid-cols-[1fr_auto] items-center gap-3'
        )}
        >
          <div className="text-left truncate">
            <SelectValue placeholder="Select language" />
          </div>
        </SelectTrigger>
        <SelectContent className={cn(
          'bg-white/95 backdrop-blur-md border border-white/20',
          'shadow-lg rounded-xl p-2',
          'min-w-[160px] w-[160px]'
        )}>
          {availableLocales.map((locale) => (
            <SelectItem 
              key={locale} 
              value={locale}
              className={cn(
                'hover:bg-white/50 focus:bg-white/50',
                'rounded-lg pl-8 pr-3 py-2.5 text-sm font-medium',
                'transition-colors duration-150',
                'cursor-pointer text-left',
                'flex items-center justify-start',
                currentLocale === locale ? 'bg-white/60 text-primary font-bold' : 'text-gray-700'
              )}
            >
              {getDisplayName(locale)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
